import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Stack,
  Pagination,
  Button,
  Badge,
  ActionIcon,
  Modal,
  Textarea,
  Skeleton,
  TextInput,
  Avatar,
  Divider,
  Grid,
  Image,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconCheck,
  IconX,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandX,
  IconBrandSpotify,
  IconSearch,
  IconRefresh,
  IconTrophy,
} from "@tabler/icons-react";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { HttpCodes } from "../../../lib/data";
import { NonPaginatedResponse } from "../../../lib/types";
import { IUserProps } from "../../../lib/types/User";

const SocialLinks = () => {
  useEffect(() => {
    document.title = "Social Links | Virel Admin";
  }, []);

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [linkActionModalOpen, setLinkActionModalOpen] = useState(false);

  // User Preview Modal State
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [viewUser, setViewUser] = useState<any>(null);
  const [fullUser, setFullUser] = useState<IUserProps>();
  const [userLoading, setUserLoading] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const form = useForm({
    initialValues: {
      reason: "",
    },
    validate: {
      reason: (value) =>
        value.trim().length < 5 ? "Reason must be at least 5 characters" : null,
    },
  });

  useEffect(() => {
    console.log("This is the user: ", fullUser);
  }, [fullUser]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data, isLoading, reloadData } = usePerformRequest<any>({
    route: Endpoints.GetSocialLinks({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter || undefined,
    }).route,
    method: "GET",
  });

  const links = Array.isArray(data?.data) ? data.data : [];
  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const handleVerify = (link: any) => {
    setSelectedLink(link);
    setVerifyModalOpen(true);
  };

  const proceedVerify = async () => {
    if (!selectedLink) return;

    setIsProcessing(true);
    const endpoint = Endpoints.VerifySocialLink(selectedLink._id);
    const res = await PerformRequest({
      route: endpoint.route,
      method: endpoint.method,
      data: { status: "approved" },
    });

    if (res.status === HttpCodes.Success) {
      reloadData();
      setVerifyModalOpen(false);
      setSelectedLink(null);
    } else {
      setErrorMessage("Failed to verify link");
      setErrorModalOpen(true);
    }
    setIsProcessing(false);
  };

  const handleReject = async (values: typeof form.values) => {
    if (!selectedLink) return;
    setIsProcessing(true);
    const endpoint = Endpoints.VerifySocialLink(selectedLink._id);
    const res = await PerformRequest({
      route: endpoint.route,
      method: endpoint.method,
      data: {
        status: "rejected",
        reason: values.reason,
      },
    });

    if (res.status === HttpCodes.Success) {
      reloadData();
      setRejectModalOpen(false);
      form.reset();
      setSelectedLink(null);
    } else {
      setErrorMessage("Failed to reject link");
      setErrorModalOpen(true);
    }
    setIsProcessing(false);
  };

  const openRejectModal = (link: any) => {
    setSelectedLink(link);
    form.reset();
    setRejectModalOpen(true);
  };

  const handleUserClick = (user: any) => {
    setViewUser(user);
    setUserModalOpen(true);
    // Fetch full user details
    fetchUserDetails(user._id);
  };

  const fetchUserDetails = async (userId: string) => {
    setUserLoading(true);
    try {
      const endpoint = Endpoints.GetUserProfile(userId);
      const res = await PerformRequest<NonPaginatedResponse<IUserProps>>({
        route: endpoint.route,
        method: endpoint.method,
      });
      if (res.status === HttpCodes.Success) {
        setFullUser(res?.data?.data);
      }
    } catch (error) {
      console.error("Failed to fetch user details", error);
    } finally {
      setUserLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge color="green">Verified</Badge>;
      case "rejected":
        return <Badge color="red">Rejected</Badge>;
      default:
        return <Badge color="yellow">Pending</Badge>;
    }
  };

  return (
    <Container size="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={1}>Social Links</Title>
            <Text c="dimmed">Manage user submitted social profiles</Text>
          </div>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group mb="md">
            <TextInput
              placeholder="Search..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: "", label: "All Statuses" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
              clearable
              style={{ width: 200 }}
            />
          </Group>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Platform</Table.Th>
                <Table.Th>Profile URL</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Submission Date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <Table.Tr key={`skeleton-${idx}`}>
                    <Table.Td>
                      <Group gap="sm">
                        <Skeleton height={32} circle />
                        <div style={{ flex: 1 }}>
                          <Skeleton height={10} width="60%" mb={4} />
                          <Skeleton height={8} width="40%" />
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={10} width="80%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={10} width="60%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={20} width={60} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={10} width="50%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={24} width={60} />
                    </Table.Td>
                  </Table.Tr>
                ))
              ) : links.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5} align="center">
                    No links found
                  </Table.Td>
                </Table.Tr>
              ) : (
                links.map((link: any) => (
                  <Table.Tr
                    key={link._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedLink(link);
                      setLinkActionModalOpen(true);
                    }}
                  >
                    <Table.Td>
                      <Group gap="sm">
                        {/* Assuming link.user is populated with firstName/lastName */}
                        <Text
                          size="sm"
                          fw={500}
                          c="green"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUserClick(link.user);
                          }}
                        >
                          {link.user?.firstName} {link.user?.lastName}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {link.platform === "instagram" ? (
                          <IconBrandInstagram size={16} />
                        ) : link.platform === "twitter" ? (
                          <IconBrandX size={16} />
                        ) : (
                          <IconBrandTiktok size={16} />
                        )}
                        <Text size="sm" tt="capitalize">
                          {link.platform}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <a
                        href={link.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#228be6", textDecoration: "none" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Visit Profile
                      </a>
                    </Table.Td>
                    <Table.Td>{getStatusBadge(link.status)}</Table.Td>
                    <Table.Td>
                      {new Date(link.createdAt).toLocaleDateString()}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          <Group justify="center" mt="md">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() => reloadData()}
            >
              Reload Links
            </Button>
          </Group>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
              />
            </Group>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Social Link"
        centered
      >
        <form onSubmit={form.onSubmit(handleReject)}>
          <Stack>
            <Text size="sm">Please provide a reason for rejection:</Text>
            <Textarea
              placeholder="Reason for rejection..."
              minRows={3}
              withAsterisk
              {...form.getInputProps("reason")}
            />
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => setRejectModalOpen(false)}
              >
                Cancel
              </Button>
              <Button color="red" type="submit" loading={isProcessing}>
                Reject
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
        title="Verify Social Link"
        centered
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to verify the social link for{" "}
            <Text span fw={700}>
              {selectedLink?.user?.firstName} {selectedLink?.user?.lastName}
            </Text>
            ?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setVerifyModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="green"
              onClick={proceedVerify}
              loading={isProcessing}
            >
              Verify Link
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={linkActionModalOpen}
        onClose={() => setLinkActionModalOpen(false)}
        title="Social Link Actions"
        centered
        size="md"
      >
        <Stack>
          <Group justify="space-between">
            <Text fw={600}>Platform:</Text>
            <Group gap={4}>
              {selectedLink?.platform === "instagram" ? (
                <IconBrandInstagram size={16} />
              ) : selectedLink?.platform === "twitter" ? (
                <IconBrandX size={16} />
              ) : (
                <IconBrandTiktok size={16} />
              )}
              <Text tt="capitalize">{selectedLink?.platform}</Text>
            </Group>
          </Group>

          <Group justify="space-between">
            <Text fw={600}>Profile URL:</Text>
            <a
              href={selectedLink?.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#228be6", textDecoration: "none" }}
            >
              {selectedLink?.profileUrl}
            </a>
          </Group>

          <Group justify="space-between">
            <Text fw={600}>Status:</Text>
            {getStatusBadge(selectedLink?.status)}
          </Group>

          <Group justify="space-between">
            <Text fw={600}>Submitted:</Text>
            <Text>
              {selectedLink
                ? new Date(selectedLink.createdAt).toLocaleDateString()
                : ""}
            </Text>
          </Group>

          <Group justify="space-between" align="center">
            <Text fw={600}>User level:</Text>
            <Badge color="yellow" variant="light">
              Level {(selectedLink?.user as IUserProps | undefined)?.level ?? 1}
            </Badge>
          </Group>

          {selectedLink?.status === "rejected" && (
            <Stack gap={4} mt="sm">
              <Text fw={600} c="red">
                Rejection Reason:
              </Text>
              <Paper
                p="sm"
                bg="red.0"
                style={{ border: "1px solid #ffccd5", borderRadius: 8 }}
              >
                <Text c="red.9" size="sm">
                  {selectedLink?.rejectionReason || "No reason provided"}
                </Text>
              </Paper>
            </Stack>
          )}

          <Divider my="sm" />

          {selectedLink?.status === "pending" ? (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Review this profile to verify if it belongs to the user.
              </Text>
              <Group>
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconX size={16} />}
                  onClick={() => {
                    setLinkActionModalOpen(false);
                    openRejectModal(selectedLink);
                  }}
                >
                  Reject
                </Button>
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={() => {
                    setLinkActionModalOpen(false);
                    handleVerify(selectedLink);
                  }}
                  loading={isProcessing}
                >
                  Approve
                </Button>
              </Group>
            </Group>
          ) : (
            <Group justify="center">
              <Text size="sm" c="dimmed" fs="italic">
                This link has already been {selectedLink?.status}. No further
                actions can be taken.
              </Text>
            </Group>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        title="User Profile Preview"
        centered
        size="lg"
      >
        {userLoading ? (
          <Stack>
            <Group>
              <Skeleton height={80} width={80} circle />
              <div style={{ flex: 1 }}>
                <Skeleton height={20} width="60%" mb="xs" />
                <Skeleton height={14} width="40%" />
              </div>
            </Group>
            <Skeleton height={8} width="100%" mt="md" />
            <Skeleton height={8} width="100%" />
            <Skeleton height={8} width="80%" />
          </Stack>
        ) : fullUser ? (
          <Stack gap="md">
            {/* Header Section */}
            <Group align="flex-start">
              <Avatar
                size={80}
                radius={80}
                color="initials"
                name={`${fullUser?.firstName || ""} ${
                  fullUser?.lastName || ""
                }`}
              />
              <div style={{ flex: 1 }}>
                <Title order={3}>
                  {fullUser?.firstName} {fullUser?.lastName}
                </Title>
                <Text c="dimmed" size="sm">
                  {fullUser?.email}
                </Text>
                <Text c="dimmed" size="sm">
                  {fullUser?.phoneNumber}
                </Text>

                <Group gap="xs" mt="xs" wrap="wrap">
                  <Badge
                    color={fullUser?.isActive ? "green" : "red"}
                    variant="light"
                  >
                    {fullUser?.isActive ? "Active" : "Inactive"}
                  </Badge>
                  {fullUser?.isSuspended && (
                    <Badge color="red" variant="filled">
                      Suspended
                    </Badge>
                  )}
                  {fullUser?.isEmailVerified && (
                    <Badge color="green" variant="dot">
                      Email Verified
                    </Badge>
                  )}
                  {fullUser?.isPhoneVerified && (
                    <Badge color="cyan" variant="dot">
                      Phone Verified
                    </Badge>
                  )}
                  <Badge
                    color="yellow"
                    variant="filled"
                    leftSection={<IconTrophy size={14} />}
                  >
                    Level {fullUser?.level ?? 1}
                  </Badge>

                </Group>
              </div>
            </Group>

            <Divider />

            {/* Financial Stats */}
            <Grid>
              <Grid.Col span={4}>
                <Paper p="xs" withBorder radius="md" bg="var(--bg-surface-2)">
                  <Text size="xs" c="dimmed">
                    Total Balance
                  </Text>
                  <Text fw={700}>
                    ₦{fullUser?.balance?.toLocaleString() || "0"}
                  </Text>
                </Paper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Paper p="xs" withBorder radius="md" bg="var(--bg-surface-2)">
                  <Text size="xs" c="dimmed">
                    Available
                  </Text>
                  <Text fw={700}>
                    ₦{fullUser?.availableBalance?.toLocaleString() || "0"}
                  </Text>
                </Paper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Paper p="xs" withBorder radius="md" bg="var(--bg-surface-2)">
                  <Text size="xs" c="dimmed">
                    Streak / Points
                  </Text>
                  <Text fw={700}>
                    {fullUser?.streakCount || 0} 🔥 / {fullUser?.points || 0}{" "}
                    pts
                  </Text>
                </Paper>
              </Grid.Col>
            </Grid>

            {/* Info Grid */}
            <Grid gutter="xs">
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Referral Code
                </Text>
                <Text fw={500} size="sm">
                  {fullUser?.referralCode || "N/A"}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Invited By
                </Text>
                <Text fw={500} size="sm">
                  {fullUser?.invitedBy || "None"}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Joined
                </Text>
                <Text fw={500} size="sm">
                  {fullUser?.createdAt
                    ? new Date(fullUser.createdAt).toLocaleDateString()
                    : "N/A"}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Last Login
                </Text>
                <Text fw={500} size="sm">
                  {fullUser?.lastLoginAt
                    ? new Date(fullUser.lastLoginAt).toLocaleString()
                    : "Never"}
                </Text>
              </Grid.Col>
            </Grid>



            {/* Bank Account */}
            {fullUser?.bankAccount && (
              <>
                <Divider label="Bank Details" labelPosition="center" />
                <Paper withBorder p="sm" radius="md">
                  <Group justify="space-between">
                    <div>
                      <Text size="sm" fw={600}>
                        {fullUser.bankAccount.bankName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {fullUser.bankAccount.accountNumber}
                      </Text>
                    </div>
                    <Badge color="gray" variant="outline">
                      {fullUser.bankAccount.accountName}
                    </Badge>
                  </Group>
                </Paper>
              </>
            )}

            {/* Third Party */}
            {fullUser?.spotify?.accessToken && (
              <>
                <Divider label="Connections" labelPosition="center" />
                <Paper withBorder p="sm" radius="md">
                  <Group>
                    <IconBrandSpotify size={20} color="#1DB954" />
                    <div>
                      <Text size="sm" fw={600}>
                        Spotify Connected
                      </Text>
                      <Text size="xs" c="dimmed">
                        Since{" "}
                        {fullUser.spotify.connectedAt
                          ? new Date(
                              fullUser.spotify.connectedAt,
                            ).toLocaleDateString()
                          : "Unknown"}
                      </Text>
                    </div>
                  </Group>
                </Paper>
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setUserModalOpen(false)}>
                Close
              </Button>
              <Button onClick={() => navigate(`/d/users/${fullUser?.id}`)}>
                View Full Profile
              </Button>
            </Group>
          </Stack>
        ) : (
          <Text c="red">Failed to load user info</Text>
        )}
      </Modal>

      {/* Error Modal */}
      <Modal
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={
          <Group gap="xs">
            <IconX size={20} color="red" />
            <Text fw={700} c="red">
              Error
            </Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text>{errorMessage}</Text>
          <Group justify="flex-end">
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default SocialLinks;
